import React from 'react';

const TEAL = '#4DB6AC';

/**
 * Render a comment's content with any `@<username>` tokens that match
 * the comment's structured `mentions[]` array styled as bold + teal
 * tags. Non-matching `@` tokens (e.g. someone typed `@` in casual
 * conversation without picking from the dropdown) render as plain text.
 *
 * Deliberately NOT navigable — there is no per-user profile route in the
 * app today. Adding one is a separate feature; when it lands, this
 * component becomes the single place to wire click-to-profile.
 *
 * Backwards compatible with old comments that have no `mentions` field:
 * default `mentions = []` means nothing gets tagged, everything renders
 * as plain text — exactly how those old comments render today.
 */
export default function renderCommentContent(content, mentions) {
  if (!content) return null;
  const list = Array.isArray(mentions) ? mentions : [];
  if (list.length === 0) return content;

  // Build a case-insensitive username → user_id lookup once per render.
  const byLower = new Map();
  for (const m of list) {
    if (m?.username) byLower.set(m.username.toLowerCase(), m);
  }

  // Split content into text + @token parts. Regex matches "@<word>"
  // where word is any non-whitespace run. We capture with the leading
  // `@` inside the group so we can re-render it exactly.
  const parts = content.split(/(@\S+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      // Strip trailing punctuation (`,`, `.`, `!`, `?`, `:`) so `Hi @Bob,`
      // still matches "Bob". The stripped punctuation is emitted as a
      // sibling text node so the visual reads identically.
      const match = part.slice(1).match(/^([^\s,.!?:;)]+)(.*)$/);
      if (match) {
        const [, name, trailing] = match;
        const found = byLower.get(name.toLowerCase());
        if (found) {
          return (
            <React.Fragment key={i}>
              <span data-testid="comment-mention-tag" style={{ color: TEAL, fontWeight: 600 }}>
                @{name}
              </span>
              {trailing}
            </React.Fragment>
          );
        }
      }
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}
